import React, { useState, FC, useEffect, useContext } from "react";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import EstimatedCostsSetupHead from "./estimatedcostssetuphead";
import EstimatedCostsSetupDetail from "./estimatedcostssetupdetail";

import { EstimatedCostsData, execRefreshConsolidationCache } from "../../../lib/api/estimatedcost";
import { updateEstimatedCostSetup } from "../../../lib/api/estimatedcostsetup";
import { SnackContext } from "../../../lib/context/SnackContext";
import { GenerateErrorMessage } from "../../../lib/helpers/string_methods";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(1),
    },
  });

type EstimatedCostsSetupProps = {
  currentData: EstimatedCostsData;
  data: any[];
  isDuplicate: boolean;
  setIsDuplicate(value: boolean): void;
  submitHeadData(values: any): Promise<number>;
} & WithStyles<typeof styles>;

const getSortedList = (str: string | string[]) => {
  if (!str) return [];
  if (typeof str == "object") {
    return str.map((s) => +s);
  }
  return str.split(",").map((s) => +s);
};

export const duplicateCheck = (row: any, data: any[], packSpecId: number) =>
  data.some(
    (item) =>
      item.id != row.id &&
      item.season == row.season &&
      item.commodity == row.commodity &&
      item.container == row.container &&
      item.conventional == row.conventional &&
      item.pack_id == packSpecId &&
      getSortedList(item.port_id).some((port) => getSortedList(row.port_id).includes(port)) &&
      getSortedList(item.countries).some((country_id) => getSortedList(row.countries).includes(country_id)),
  );

const EstimatedCostsSetupUnstyled: FC<EstimatedCostsSetupProps> = (props) => {
  const { classes, currentData, data, isDuplicate, setIsDuplicate, submitHeadData } = props;

  const { updateSnack } = useContext(SnackContext);

  const [estimatedcostid, setEstimatedcostid] = useState(currentData.original_id);
  const [packspec_id, setPackspec_id] = useState(currentData.pack_id);

  useEffect(() => {
    setEstimatedcostid(currentData.original_id);
    setPackspec_id(currentData.pack_id);
  }, [currentData]);

  const submitSetupDetail = async (packSpecId: number, detail: any[]): Promise<number | undefined> => {
    let id = undefined;
    try {
      const duplicate = duplicateCheck(currentData, data, packSpecId);

      if (!duplicate) {
        id = await submitHeadData({ ...currentData, pack_id: packSpecId });

        const newDetail = detail.map((item) => ({
          estimatedcost_id: id,
          estimatedcostitem_id: item.estimatedcostitem_id,
          pack_id: packSpecId,
          value: item.value,
          variety: item.variety == "ALL" ? null : item.variety,
        }));

        await updateEstimatedCostSetup(newDetail, id);
        setEstimatedcostid(id);
        setPackspec_id(packSpecId);
        execRefreshConsolidationCache();
      } else {
        setIsDuplicate(true);
        return undefined;
      }
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error updating data");
      updateSnack({ show: true, color: "red", message: err });
    }
    return id;
  };

  return (
    <div className={classes.root}>
      <EstimatedCostsSetupHead
        packspec_id={packspec_id}
        currentHead={currentData}
        allData={data}
        setIsDuplicate={setIsDuplicate}
        isDuplicate={isDuplicate}
        submitHeadData={submitHeadData}
      />
      {estimatedcostid && estimatedcostid != 0 && estimatedcostid != -1 && (
        <EstimatedCostsSetupDetail
          estimatedcost_id={estimatedcostid}
          packspec_id={packspec_id}
          submitSetupDetail={submitSetupDetail}
          isDuplicate={isDuplicate}
          currentHead={currentData}
          allPacks={data}
          setIsDuplicate={setIsDuplicate}
        />
      )}
    </div>
  );
};

export default withStyles(styles)(EstimatedCostsSetupUnstyled);
